"use client"

import { useSyncExternalStore } from "react"

import type { WishlistGateway } from "@/platform/api/gateways/wishlist"
import { logger } from "@/platform/observability/logger"
import { holdViewerBoundary, viewerBoundaryGeneration, SupersededViewerError } from "@/platform/session/viewer-boundary"

const STORAGE_KEY = "nelo.wishlist"

export interface StoredWishlist {
  id: string
  claimed: boolean
}

export const parseStoredWishlist = (raw: string | null): StoredWishlist | null => {
  if (!raw) return null
  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== "object" || parsed === null) return null
    const { id, claimed } = parsed as { id?: unknown; claimed?: unknown }
    if (typeof id !== "string" || id.length === 0) return null
    return { id, claimed: claimed === true }
  } catch {
    return null
  }
}

let cached: StoredWishlist | null = null
let hydrated = false
const listeners = new Set<() => void>()

const hydrate = () => {
  if (hydrated || typeof window === "undefined") return
  hydrated = true
  try {
    cached = parseStoredWishlist(window.localStorage.getItem(STORAGE_KEY))
  } catch {
    cached = null
  }
}

export const getStoredWishlist = (): StoredWishlist | null => {
  hydrate()
  return cached
}

export const setStoredWishlist = (entry: StoredWishlist | null) => {
  hydrated = true
  cached = entry
  if (typeof window !== "undefined") {
    try {
      if (entry) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entry))
      else window.localStorage.removeItem(STORAGE_KEY)
    } catch {
      // Storage can be unavailable (private mode, quota); the in-memory copy still applies.
    }
  }
  listeners.forEach((listener) => listener())
}

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

const getServerSnapshot = (): StoredWishlist | null => null

export const useWishlistId = (): string | null => {
  const entry = useSyncExternalStore(subscribe, getStoredWishlist, getServerSnapshot)
  return entry?.id ?? null
}

let createInFlight: Promise<string> | null = null

export const ensureWishlistId = (claimed: boolean, wishlists: Pick<WishlistGateway, "create">): Promise<string> => {
  const stored = getStoredWishlist()
  if (stored) return Promise.resolve(stored.id)
  if (createInFlight) return createInFlight
  const held = holdViewerBoundary()
  const promise = wishlists
    .create()
    .then((created) => {
      if (!created.id) throw new Error("Wishlist create answered without an id")
      if (!held()) throw new SupersededViewerError()
      setStoredWishlist({ id: created.id, claimed })
      return created.id
    })
    .finally(() => {
      if (createInFlight === promise) createInFlight = null
    })
  createInFlight = promise
  return promise
}

type WishlistOwnershipClient = Pick<WishlistGateway, "claim" | "mine">

interface WishlistOwnershipSession {
  owner: string | null
  generation: number
}

let lastSyncedSession: WishlistOwnershipSession | null = null
let syncInFlight: (WishlistOwnershipSession & { promise: Promise<boolean> }) | null = null

const applyOwnership = async (owner: string | null, client: WishlistOwnershipClient, held: () => boolean): Promise<boolean> => {
  const stored = getStoredWishlist()
  if (!owner) {
    if (!stored?.claimed) return false
    setStoredWishlist(null)
    return true
  }
  if (stored?.claimed) return false
  if (stored) {
    const claimed = await client.claim(stored.id)
    if (!held()) {
      if (getStoredWishlist() === stored) setStoredWishlist(null)
      return false
    }
    setStoredWishlist({ id: claimed.id || stored.id, claimed: true })
    return true
  }
  const mine = await client.mine()
  if (!held() || !mine?.id) return false
  setStoredWishlist({ id: mine.id, claimed: true })
  return true
}

export const syncWishlistOwnership = (owner: string | null, client: WishlistOwnershipClient): Promise<boolean> => {
  const generation = viewerBoundaryGeneration()
  if (owner === lastSyncedSession?.owner && generation === lastSyncedSession.generation) return Promise.resolve(false)
  if (syncInFlight?.owner === owner && syncInFlight.generation === generation) return syncInFlight.promise
  const isCurrent = holdViewerBoundary()
  const promise = applyOwnership(owner, client, isCurrent)
    .then((changed) => {
      if (isCurrent()) lastSyncedSession = { owner, generation }
      return changed
    })
    .catch((error: unknown) => {
      if (isCurrent() && !(error instanceof SupersededViewerError)) logger.error("Wishlist ownership sync failed", {}, error as Error)
      return false
    })
    .finally(() => {
      if (syncInFlight?.promise === promise) syncInFlight = null
    })
  syncInFlight = { owner, generation, promise }
  return promise
}

export const resetWishlistOwnership = (): void => {
  createInFlight = null
  lastSyncedSession = null
  syncInFlight = null
}
